import React, { memo } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import styles from "./style.module.scss";
import { FaTelegram, FaInstagram, FaFacebookF } from "react-icons/fa6";
import { Collapse } from "antd";
import { IoChevronDown } from "react-icons/io5";
import useResponsive from "~/shared/hooks/useResponsive";
import { useTranslation } from "next-i18next";

const AbousUsLinks = [
    {
        key: "about_us",
        link: "/page/about-us",
    },
    {
        key: "video_guides",
        link: "/page/become-a-seller",
    },
    {
        key: "public_offer",
        link: "/oferta",
    }
]

const ServiceLinks = [
    {
        key: "scientific_services",
        link: "https://soff.uz/orders?direction=scientific_work",
    },
    {
        key: "design",
        link: "https://soff.uz/orders?direction=dizayn",
    },
    {
        key: "programming_it",
        link: "https://soff.uz/orders?direction=web",
    },
    {
        key: "three_d_design",
        link: "https://soff.uz/orders?direction=three_d",
    },
    {
        key: "marketing_smm",
        link: "https://soff.uz/orders?direction=marketing"
    },
    {
        key: "audio_video",
        link: "https://soff.uz/orders?direction=audio_video"
    },
    {
        key: "business",
        link: "https://soff.uz/orders?direction=business"
    }
]

const ProductLink = [
    {
        key: "scientific_works",
        link: "https://soff.uz/scientific-resources/all?slug=all",
    },
    {
        key: "three_d_design",
        link: "https://soff.uz/3d-models-and-interior-designs/all?slug=all",
    },
    {
        key: "design_templates",
        link: "https://soff.uz/design-developments/all?slug=all",
    },
    {
        key: "industry_templates",
        link: "https://soff.uz/templates/all?slug=all",
    },
    {
        key: "video_lessons",
        link: "https://soff.uz/video-lessons/all?slug=all",
    },
    {
        key: "programming_services",
        link: "https://soff.uz/websites/all?slug=all",
    },
]

// Link list component
const LinkList: React.FC<{ links: { title: string; link: string }[]; external?: boolean }> = ({ links, external }) => (
    <ul>
        {links.map((el) => (
            <li key={el.title}>
                <a href={el.link} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>
                    {el.title}
                </a>
            </li>
        ))}
    </ul>
);

// Contact section component
const ContactSection: React.FC<{ labels: { title: string; moderation: string; technical: string } }> = ({ labels }) => (
    <div className={styles.contact}>
        <h4>{labels.title}</h4>
        <p>{labels.moderation}</p>
        <a href="tel:+998910086789">+998 (91) 008 67 89</a>
        <a href="https://telegram.me/soff_moderator" target="_blank" rel="noreferrer">@soff_moderator</a>
        <p>{labels.technical}</p>
        <a href="https://telegram.me/hr_soffhub" target="_blank" rel="noreferrer">@hr_soffhub</a>
    </div>
);

const Footer: React.FC = () => {
    const { isMobile, isTablet, isDesktop } = useResponsive();
    const { pathname } = useRouter();
    const { t } = useTranslation("common");
    const showAccordion = isMobile || isTablet;
    
    // Account sahifalarida bottom navigation bor, shuning uchun qo'shimcha padding kerak
    const isAccountPage = pathname.startsWith('/account');
    const hasBottomNav = !isDesktop && isAccountPage;

    const aboutUsLinks = AbousUsLinks.map((item) => ({
        title: t(`footer.about.links.${item.key}`),
        link: item.link,
    }));

    const serviceLinks = ServiceLinks.map((item) => ({
        title: t(`footer.services.links.${item.key}`),
        link: item.link,
    }));

    const productLinks = ProductLink.map((item) => ({
        title: t(`footer.products.links.${item.key}`),
        link: item.link,
    }));

    const accordionItems = [
        {
            key: 'contact',
            label: t('footer.contact.title'),
            children: (
                <div className={styles.accordionContent}>
                    <div className={styles.contactMobile}>
                        <p>{t('footer.contact.moderation')}</p>
                        <a href="tel:+998910086789">+998 (91) 008 67 89</a>
                        <a href="https://telegram.me/soff_moderator" target="_blank" rel="noreferrer">@soff_moderator</a>
                        <p>{t('footer.contact.technical')}</p>
                        <a href="https://telegram.me/hr_soffhub" target="_blank" rel="noreferrer">@hr_soffhub</a>
                    </div>
                </div>
            ),
        },
        {
            key: 'about',
            label: t('footer.about.title'),
            children: (
                <div className={styles.accordionContent}>
                    <LinkList links={aboutUsLinks} />
                </div>
            ),
        },
        {
            key: 'products',
            label: t('footer.products.title'),
            children: (
                <div className={styles.accordionContent}>
                    <LinkList links={productLinks} external />
                </div>
            ),
        },
        {
            key: 'services',
            label: t('footer.services.title'),
            children: (
                <div className={styles.accordionContent}>
                    <LinkList links={serviceLinks} external />
                </div>
            ),
        },
    ];

    return (
        <footer className={`${styles.footer} ${hasBottomNav ? styles.withBottomNav : ''}`}>
            <div className={styles.container}>
                {/* Logo va ijtimoiy tarmoqlar */}
                <div className={styles.logoSection}>
                    <div className={styles.logoBox}>
                        <img
                            src="/static/img/soff-logos/soff_logo_white.webp"
                            alt="Soff logo"
                            className={styles.logo}
                        />
                        <p className={styles.desc}>
                            {t('footer.tagline')}
                        </p>
                    </div>
                    <div className={styles.socials}>
                        <a href="https://telegram.me/soff_moderator" target="_blank" rel="noreferrer">
                            <FaTelegram />
                        </a>
                        <a href="https://www.facebook.com/people/Soffuz/61579052952962/" target="_blank" rel="noreferrer">
                            <FaFacebookF />
                        </a>
                        <a href="https://www.instagram.com/soff_uz_/" target="_blank" rel="noreferrer">
                            <FaInstagram />
                        </a>
                    </div>
                </div>

                {/* Mobile/Tablet Accordion */}
                {showAccordion ? (
                    <div className={styles.accordionWrapper}>
                        <Collapse
                            defaultActiveKey={"contact"}
                            accordion
                            ghost
                            expandIconPosition="end"
                            expandIcon={({ isActive }) => (
                                <IoChevronDown
                                    className={`${styles.accordionIcon} ${isActive ? styles.active : ''}`}
                                />
                            )}
                            items={accordionItems}
                            className={styles.footerAccordion}
                        />
                    </div>
                ) : (
                    /* Desktop Grid */
                    <div className={styles.grid}>
                        {/* Aloqa va Biz haqimizda */}
                        <div className={styles.col}>
                            <ContactSection
                                labels={{
                                    title: t('footer.contact.title'),
                                    moderation: t('footer.contact.moderation'),
                                    technical: t('footer.contact.technical'),
                                }}
                            />
                            <h4>{t('footer.about.title')}</h4>
                            <LinkList links={aboutUsLinks} />
                        </div>

                        {/* Tayyor mahsulotlar */}
                        <div className={styles.col}>
                            <h4>{t('footer.products.title')}</h4>
                            <LinkList links={productLinks} external />
                        </div>

                        {/* Xizmat turlari */}
                        <div className={styles.col}>
                            <h4>{t('footer.services.title')}</h4>
                            <LinkList links={serviceLinks} external />
                        </div>
                    </div>
                )}

                <div className={styles.bottom}>
                    <p>{t('footer.copyright')}</p>
                    <div className={styles.links}>
                        <Link href="/legal/user-agreement">
                            <a>{t('footer.legal.user_agreement')}</a>
                        </Link>
                        <span className={styles.separator}>|</span>
                        <Link href="/legal/privacy-policy">
                            <a>{t('footer.legal.privacy_policy')}</a>
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default memo(Footer);
